import { lazy, Suspense } from "react";

import { Skeleton } from "@/components/Skeleton/Skeleton";

import type { CalendarBaseProps } from "./CalendarBase";

const CalendarBase = lazy(() => import("./CalendarBase"));

const MonthSkeletonBlock = ({ hideMonthName }: { hideMonthName?: boolean }) => (
  <div>
    {!hideMonthName && (
      <div className="mb-3 h-6 w-40">
        <Skeleton variant="rounded" width={160} height={24} />
      </div>
    )}
    <Skeleton variant="rounded" width="100%" height={260} />
  </div>
);

function CalendarBaseSkeleton({
  numberOfMonths,
}: Pick<CalendarBaseProps, "numberOfMonths">) {
  const scrollable = Boolean(numberOfMonths && numberOfMonths > 1);

  return (
    <div className="w-full max-w-90">
      {scrollable ? (
        <>
          <div className="mb-4 shrink-0 border-b border-stroke-neutral-soft px-2 pb-2">
            <Skeleton variant="rounded" width="100%" height={22} />
          </div>
          <div className="min-h-0 shrink grow basis-auto space-y-8 overflow-hidden">
            <MonthSkeletonBlock />
            <MonthSkeletonBlock />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 py-2">
            <Skeleton variant="circular" width={36} height={36} />
            <div className="flex flex-1 items-center justify-center">
              <Skeleton variant="rounded" width={160} height={24} />
            </div>
            <Skeleton variant="circular" width={36} height={36} />
          </div>
          <MonthSkeletonBlock hideMonthName />
        </>
      )}
    </div>
  );
}

function CalendarBaseLazy({ numberOfMonths, ...props }: CalendarBaseProps) {
  return (
    <Suspense
      fallback={<CalendarBaseSkeleton numberOfMonths={numberOfMonths} />}
    >
      <CalendarBase numberOfMonths={numberOfMonths} {...props} />
    </Suspense>
  );
}

export { CalendarBaseLazy as CalendarBase };
